import React, { useCallback, useEffect, useRef, useState } from "react";
import { listarCompras, crearCompra, registrarEnContifico } from "../../../services/comprasDocentesService";
import { listarComprasContifico, listarProductosContifico } from "../../../services/contificoService";
import { listarEmpleados } from "../../../services/empleadosService";
import { normalizeEmpleado } from "../empleados/adapters/empleadosAdapter";
import { listarCursos } from "../../../services/cursosService";
import { showToast } from "../../../utils/showToast";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../../styles/dashboardTheme.css";
import "../../../styles/managementTheme.css";

// OJO: nunca usar new Date().toISOString().slice(0,10) para "hoy" — toISOString()
// convierte a UTC, y en Ecuador (UTC-5) desde las 19:00 ya marca el dia siguiente.
const hoy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const primerDiaMes = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
};
const fmt = (v) => Number(v || 0).toFixed(2);

const ESTADO_BADGE_LOCAL = {
  pendiente:  { label: "Pendiente",  cls: "bg-warning-subtle text-warning" },
  registrado: { label: "Registrado", cls: "bg-success-subtle text-success" },
};

const ESTADO_PRO = {
  P: { label: "Pendiente",   cls: "bg-warning-subtle text-warning" },
  G: { label: "Autorizado",  cls: "bg-success-subtle text-success" },
  A: { label: "Anulado",     cls: "bg-secondary-subtle text-secondary" },
};

// ── Modal: Nueva compra docente ───────────────────────────────────────────────

function NuevaCompraDocenteModal({ open, onClose, onCreada, empleados, cursos }) {
  const [form, setForm] = useState({
    empleado_id: "", curso_id: "", descripcion: "",
    horas: "", tarifa_hora: "", fecha: hoy(),
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ empleado_id: "", curso_id: "", descripcion: "", horas: "", tarifa_hora: "", fecha: hoy() });
  }, [open]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const subtotal = (Number(form.horas) * Number(form.tarifa_hora)) || 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.empleado_id) return showToast({ type: "warning", message: "Selecciona un docente." });
    if (!form.horas || Number(form.horas) <= 0) return showToast({ type: "warning", message: "Las horas deben ser mayores a 0." });
    if (!form.tarifa_hora || Number(form.tarifa_hora) <= 0) return showToast({ type: "warning", message: "La tarifa debe ser mayor a 0." });
    if (!form.descripcion.trim()) return showToast({ type: "warning", message: "La descripción es requerida." });
    setSaving(true);
    try {
      const nueva = await crearCompra({
        tipo:        "docente",
        empleado_id: Number(form.empleado_id),
        curso_id:    form.curso_id ? Number(form.curso_id) : null,
        descripcion: form.descripcion.trim(),
        horas:       Number(form.horas),
        tarifa_hora: Number(form.tarifa_hora),
        fecha:       form.fecha,
      });
      showToast({ type: "success", message: "Compra registrada correctamente." });
      onCreada?.(nueva);
      onClose();
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo registrar la compra." });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const docentes = empleados.filter(e =>
    e.tipoPersonal === "docente" || e.tipoContratacion === "servicios" || e.tipoContratacion === "hibrido"
  );

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(15,23,42,0.45)" }} role="dialog">
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header border-0">
              <h5 className="modal-title">
                <i className="bi bi-mortarboard me-2 text-primary" />
                Nueva compra — Docente
              </h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Docente <span className="text-danger">*</span></label>
                  <select className="form-select" value={form.empleado_id} onChange={e => set("empleado_id", e.target.value)} required>
                    <option value="">Selecciona un docente...</option>
                    {docentes.map(e => (
                      <option key={e.id} value={e.id}>
                        {e.apellidos}, {e.nombres}
                        {!e.contificoPersonaId && " ⚠ sin sync"}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Curso (opcional)</label>
                  <select className="form-select" value={form.curso_id} onChange={e => set("curso_id", e.target.value)}>
                    <option value="">Sin curso específico</option>
                    {cursos.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.nombre}
                        {!c.contifico_producto_id && " ⚠ sin sync"}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Descripción <span className="text-danger">*</span></label>
                  <input className="form-control" value={form.descripcion}
                    onChange={e => set("descripcion", e.target.value)}
                    placeholder="Ej: Docencia — Curso de Contabilidad Básica" />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Horas <span className="text-danger">*</span></label>
                  <input type="number" className="form-control" min="0.5" step="0.5"
                    value={form.horas} onChange={e => set("horas", e.target.value)} placeholder="40" />
                </div>
                <div className="col-md-4">
                  <label className="form-label">Tarifa/hora <span className="text-danger">*</span></label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input type="number" className="form-control" min="0" step="0.01"
                      value={form.tarifa_hora} onChange={e => set("tarifa_hora", e.target.value)} placeholder="5.00" />
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Fecha</label>
                  <input type="date" className="form-control" value={form.fecha} onChange={e => set("fecha", e.target.value)} />
                </div>
              </div>
              {subtotal > 0 && (
                <div className="alert alert-info mt-3 mb-0 py-2">
                  <strong>Total: ${subtotal.toFixed(2)}</strong>
                  <span className="text-muted ms-2">({form.horas} h × ${Number(form.tarifa_hora).toFixed(2)})</span>
                </div>
              )}
            </div>
            <div className="modal-footer border-0">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? <><span className="spinner-border spinner-border-sm me-2" />Guardando...</> : "Guardar"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Modal: Nueva compra general ───────────────────────────────────────────────

function NuevaCompraGeneralModal({ open, onClose, onCreada, empleados, productos }) {
  const IVA_OPTS = [0, 5, 15];

  const [form, setForm] = useState({
    tipoProveedor:          "externo", // "empleado" | "externo"
    empleado_id:            "",
    proveedor_nombre:       "",
    proveedor_identificacion: "",
    numero_factura_proveedor: "",
    descripcion:            "",
    subtotal:               "",
    iva_porcentaje:         0,
    contifico_producto_id:  "",
    fecha:                  hoy(),
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({
      tipoProveedor: "externo", empleado_id: "", proveedor_nombre: "",
      proveedor_identificacion: "", numero_factura_proveedor: "",
      descripcion: "", subtotal: "", iva_porcentaje: 0,
      contifico_producto_id: "", fecha: hoy(),
    });
  }, [open]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const subtotalNum  = Number(form.subtotal) || 0;
  const ivaValor     = Math.round(subtotalNum * Number(form.iva_porcentaje) / 100 * 100) / 100;
  const total        = subtotalNum + ivaValor;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.descripcion.trim()) return showToast({ type: "warning", message: "La descripción es requerida." });
    if (!form.subtotal || subtotalNum <= 0) return showToast({ type: "warning", message: "El subtotal debe ser mayor a 0." });
    if (form.tipoProveedor === "externo" && !form.proveedor_nombre.trim())
      return showToast({ type: "warning", message: "El nombre del proveedor es requerido." });
    if (form.tipoProveedor === "empleado" && !form.empleado_id)
      return showToast({ type: "warning", message: "Selecciona un empleado." });

    setSaving(true);
    try {
      const payload = {
        tipo:                   "general",
        descripcion:            form.descripcion.trim(),
        numero_factura_proveedor: form.numero_factura_proveedor.trim() || null,
        subtotal:               subtotalNum,
        iva_porcentaje:         Number(form.iva_porcentaje),
        iva_valor:              ivaValor,
        total,
        contifico_producto_id:  form.contifico_producto_id || null,
        fecha:                  form.fecha,
      };
      if (form.tipoProveedor === "empleado") {
        payload.empleado_id = Number(form.empleado_id);
      } else {
        payload.proveedor_nombre         = form.proveedor_nombre.trim();
        payload.proveedor_identificacion = form.proveedor_identificacion.trim() || null;
      }

      const nueva = await crearCompra(payload);
      showToast({ type: "success", message: "Compra registrada correctamente." });
      onCreada?.(nueva);
      onClose();
    } catch (err) {
      showToast({ type: "danger", message: err.message || "No se pudo registrar la compra." });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(15,23,42,0.45)" }} role="dialog">
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header border-0">
              <h5 className="modal-title">
                <i className="bi bi-bag-plus me-2 text-success" />
                Nueva compra — General
              </h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <div className="row g-3">
                {/* Tipo de proveedor */}
                <div className="col-12">
                  <label className="form-label">Proveedor <span className="text-danger">*</span></label>
                  <div className="d-flex gap-3 mb-2">
                    {[
                      { v: "externo",  label: "Externo (texto libre)" },
                      { v: "empleado", label: "Empleado registrado" },
                    ].map(({ v, label }) => (
                      <div className="form-check" key={v}>
                        <input className="form-check-input" type="radio" id={`tp-${v}`}
                          checked={form.tipoProveedor === v} onChange={() => set("tipoProveedor", v)} />
                        <label className="form-check-label" htmlFor={`tp-${v}`}>{label}</label>
                      </div>
                    ))}
                  </div>

                  {form.tipoProveedor === "externo" ? (
                    <div className="row g-2">
                      <div className="col-md-7">
                        <input className="form-control" placeholder="Nombre del proveedor *"
                          value={form.proveedor_nombre} onChange={e => set("proveedor_nombre", e.target.value)} />
                      </div>
                      <div className="col-md-5">
                        <input className="form-control" placeholder="Cédula / RUC"
                          value={form.proveedor_identificacion} onChange={e => set("proveedor_identificacion", e.target.value)} />
                      </div>
                    </div>
                  ) : (
                    <select className="form-select" value={form.empleado_id} onChange={e => set("empleado_id", e.target.value)}>
                      <option value="">Selecciona un empleado...</option>
                      {empleados.map(e => (
                        <option key={e.id} value={e.id}>
                          {e.apellidos}, {e.nombres}
                          {!e.contificoPersonaId && " ⚠ sin sync"}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Nº factura */}
                <div className="col-md-4">
                  <label className="form-label">N° Factura proveedor</label>
                  <input className="form-control font-monospace" placeholder="001-002-000000070"
                    value={form.numero_factura_proveedor} onChange={e => set("numero_factura_proveedor", e.target.value)} />
                </div>

                {/* Fecha */}
                <div className="col-md-4">
                  <label className="form-label">Fecha</label>
                  <input type="date" className="form-control" value={form.fecha} onChange={e => set("fecha", e.target.value)} />
                </div>

                {/* IVA */}
                <div className="col-md-4">
                  <label className="form-label">IVA %</label>
                  <select className="form-select" value={form.iva_porcentaje} onChange={e => set("iva_porcentaje", Number(e.target.value))}>
                    {IVA_OPTS.map(v => <option key={v} value={v}>{v}%</option>)}
                  </select>
                </div>

                {/* Descripción */}
                <div className="col-12">
                  <label className="form-label">Descripción <span className="text-danger">*</span></label>
                  <input className="form-control" value={form.descripcion}
                    onChange={e => set("descripcion", e.target.value)}
                    placeholder="Ej: Compra de materiales de oficina" />
                </div>

                {/* Montos */}
                <div className="col-md-4">
                  <label className="form-label">Subtotal (sin IVA) <span className="text-danger">*</span></label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input type="number" className="form-control" min="0.01" step="0.01"
                      value={form.subtotal} onChange={e => set("subtotal", e.target.value)} placeholder="0.00" />
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label">IVA ({form.iva_porcentaje}%)</label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input className="form-control bg-light" readOnly value={ivaValor.toFixed(2)} />
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Total</label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>
                    <input className="form-control bg-light fw-semibold" readOnly value={total.toFixed(2)} />
                  </div>
                </div>

                {/* Producto Contífico (opcional) */}
                <div className="col-12">
                  <label className="form-label">
                    Producto Contífico
                    <span className="text-muted ms-2" style={{ fontSize: 12 }}>(requerido para enviar a Contífico)</span>
                  </label>
                  <select className="form-select" value={form.contifico_producto_id}
                    onChange={e => set("contifico_producto_id", e.target.value)}>
                    <option value="">Sin producto (no se podrá enviar a Contífico)</option>
                    {productos.map(p => (
                      <option key={p.id} value={p.id}>{p.codigo} — {p.nombre}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer border-0">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancelar</button>
              <button type="submit" className="btn btn-success" disabled={saving}>
                {saving ? <><span className="spinner-border spinner-border-sm me-2" />Guardando...</> : "Guardar"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Tabla de compras locales ──────────────────────────────────────────────────

function TablaLocal({ compras, loading, enviandoId, onEnviar }) {
  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>;
  if (!compras.length) {
    return (
      <div className="text-center py-5 text-muted">
        <i className="bi bi-file-earmark-text fs-1 d-block mb-2 opacity-25" />
        No hay compras registradas.
      </div>
    );
  }

  return (
    <div className="management-table-wrapper">
      <table className="table align-middle management-table mb-0">
        <thead>
          <tr>
            <th>Tipo</th>
            <th>Proveedor</th>
            <th>Descripción</th>
            <th>N° Factura</th>
            <th className="text-end">Total</th>
            <th>Fecha</th>
            <th>Estado</th>
            <th className="text-end">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {compras.map(c => {
            const badge = ESTADO_BADGE_LOCAL[c.estado] || { label: c.estado, cls: "bg-secondary-subtle text-secondary" };
            const monto = Number(c.monto_total || 0);
            return (
              <tr key={c.id}>
                <td>
                  {c.tipo === "docente"
                    ? <span className="badge bg-primary-subtle text-primary"><i className="bi bi-mortarboard me-1" />Docente</span>
                    : <span className="badge bg-success-subtle text-success"><i className="bi bi-bag me-1" />General</span>
                  }
                </td>
                <td>
                  <div className="fw-semibold">{c.empleado_nombre || "—"}</div>
                  <small className="text-muted">{c.empleado_cedula || ""}</small>
                </td>
                <td>
                  <div>{c.descripcion}</div>
                  {c.tipo === "docente" && c.curso_nombre && (
                    <small className="text-muted"><i className="bi bi-mortarboard me-1" />{c.curso_nombre}</small>
                  )}
                  {c.tipo === "docente" && (
                    <small className="text-muted d-block">{Number(c.horas).toFixed(1)} h × ${Number(c.tarifa_hora).toFixed(2)}</small>
                  )}
                </td>
                <td>
                  <small className="font-monospace text-muted">{c.numero_factura_proveedor || "—"}</small>
                </td>
                <td className="text-end fw-semibold">${monto.toFixed(2)}</td>
                <td>{c.fecha ? String(c.fecha).substring(0, 10) : "—"}</td>
                <td><span className={`badge ${badge.cls}`}>{badge.label}</span></td>
                <td className="text-end">
                  {c.estado === "pendiente" ? (
                    <button type="button" className="btn btn-sm btn-outline-primary"
                      onClick={() => onEnviar(c)} disabled={enviandoId === c.id}
                      title="Registrar en Contífico">
                      {enviandoId === c.id
                        ? <span className="spinner-border spinner-border-sm" />
                        : <><i className="bi bi-cloud-upload me-1" />Enviar</>}
                    </button>
                  ) : (
                    <small className="text-muted" title={c.contifico_documento_id}>
                      <i className="bi bi-cloud-check-fill text-success me-1" />
                      {c.contifico_documento_id?.substring(0, 8)}…
                    </small>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Modal detalle PRO Contífico ───────────────────────────────────────────────

export function DetallePROModal({ doc, onClose }) {
  if (!doc) return null;
  const prov = doc._proveedor || {};
  const est  = ESTADO_PRO[doc.estado] || { label: doc.estado || "—", cls: "bg-secondary-subtle text-secondary" };
  const retenciones    = Array.isArray(doc.retenciones) ? doc.retenciones : [];
  const totalRetencion = retenciones.reduce((s, r) => s + Number(r.valor || r.monto || 0), 0);

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(15,23,42,0.45)" }} role="dialog">
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header border-0 pb-0">
            <div>
              <h5 className="modal-title mb-0"><i className="bi bi-receipt me-2" />{doc.documento || "Compra PRO"}</h5>
              <small className="text-muted">{doc.fecha_emision || "—"}</small>
            </div>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <p className="text-muted mb-1 fw-semibold" style={{ fontSize: 11 }}>PROVEEDOR</p>
            <div className="d-flex align-items-start justify-content-between mb-3">
              <div>
                <div className="fw-semibold">
                  {prov.nombre || "—"}
                  {prov.esDocente && (
                    <span className="badge bg-info-subtle text-info ms-2" style={{ fontSize: 10 }}>
                      <i className="bi bi-mortarboard me-1" />Docente Ademy
                    </span>
                  )}
                </div>
                {prov.identificacion && <small className="text-muted">{prov.identificacion}</small>}
              </div>
              <span className={`badge ${est.cls}`}>{est.label}</span>
            </div>

            {doc.descripcion && (
              <>
                <p className="text-muted mb-1 fw-semibold" style={{ fontSize: 11 }}>DESCRIPCIÓN</p>
                <p className="mb-3">{doc.descripcion}</p>
              </>
            )}

            {Array.isArray(doc.detalles) && doc.detalles.length > 0 && (
              <>
                <p className="text-muted mb-1 fw-semibold" style={{ fontSize: 11 }}>DETALLE</p>
                <table className="table table-sm table-bordered mb-3">
                  <thead className="table-light">
                    <tr>
                      <th>Producto / Servicio</th>
                      <th className="text-end">Cant.</th>
                      <th className="text-end">Precio U.</th>
                      <th className="text-end">IVA %</th>
                      <th className="text-end">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doc.detalles.map((d, i) => (
                      <tr key={i}>
                        <td>{d.producto || d.producto_nombre || d.descripcion || "—"}</td>
                        <td className="text-end">{Number(d.cantidad || 0).toFixed(1)}</td>
                        <td className="text-end">${fmt(d.precio)}</td>
                        <td className="text-end">{d.iva ?? 0}%</td>
                        <td className="text-end">${fmt(Number(d.cantidad || 0) * Number(d.precio || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <p className="text-muted mb-1 fw-semibold" style={{ fontSize: 11 }}>MONTOS</p>
            <table className="table table-sm table-borderless mb-3" style={{ maxWidth: 320, marginLeft: "auto" }}>
              <tbody>
                {Number(doc.subtotal_0 || 0) > 0 && (
                  <tr><td className="text-muted">Subtotal 0%</td><td className="text-end">${fmt(doc.subtotal_0)}</td></tr>
                )}
                {Number(doc.subtotal_12 || doc.subtotal || 0) > 0 && (
                  <tr><td className="text-muted">Subtotal {doc.porcentaje_iva || 15}%</td><td className="text-end">${fmt(doc.subtotal_12 || doc.subtotal)}</td></tr>
                )}
                {Number(doc.iva || 0) > 0 && (
                  <tr><td className="text-muted">IVA</td><td className="text-end">${fmt(doc.iva)}</td></tr>
                )}
                <tr className="fw-bold border-top">
                  <td>Total</td><td className="text-end">${fmt(doc.total)}</td>
                </tr>
                {totalRetencion > 0 && (
                  <tr className="text-danger">
                    <td>Retención</td><td className="text-end">- ${fmt(totalRetencion)}</td>
                  </tr>
                )}
                {Number(doc.saldo || 0) > 0 && (
                  <tr className="fw-semibold">
                    <td>Saldo</td><td className="text-end">${fmt(doc.saldo)}</td>
                  </tr>
                )}
              </tbody>
            </table>

            {retenciones.length > 0 && (
              <>
                <p className="text-muted mb-1 fw-semibold" style={{ fontSize: 11 }}>RETENCIONES</p>
                <table className="table table-sm table-bordered mb-0">
                  <thead className="table-light">
                    <tr><th>Concepto</th><th className="text-end">Base</th><th className="text-end">%</th><th className="text-end">Valor</th></tr>
                  </thead>
                  <tbody>
                    {retenciones.map((r, i) => (
                      <tr key={i}>
                        <td>{r.descripcion || r.tipo || `Ret. ${i + 1}`}</td>
                        <td className="text-end">${fmt(r.base)}</td>
                        <td className="text-end">{r.porcentaje ?? r.tasa ?? "—"}%</td>
                        <td className="text-end">${fmt(r.valor || r.monto)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
          <div className="modal-footer border-0">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tabla PRO Contífico ───────────────────────────────────────────────────────

function TablaContifico({ compras, loading, onVerDetalle }) {
  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>;
  if (!compras.length) {
    return (
      <div className="text-center py-5 text-muted">
        <i className="bi bi-cloud fs-1 d-block mb-2 opacity-25" />
        No se encontraron compras en Contífico para el período seleccionado.
      </div>
    );
  }
  return (
    <div className="management-table-wrapper">
      <table className="table align-middle management-table mb-0">
        <thead>
          <tr>
            <th>Proveedor</th>
            <th>Descripción</th>
            <th className="text-end">Total</th>
            <th>Fecha</th>
            <th>N° Documento</th>
            <th>Estado</th>
            <th className="text-end">Ver</th>
          </tr>
        </thead>
        <tbody>
          {compras.map(c => {
            const est  = ESTADO_PRO[c.estado] || { label: c.estado || "—", cls: "bg-secondary-subtle text-secondary" };
            const prov = c._proveedor || {};
            return (
              <tr key={c.id} style={{ cursor: "pointer" }}
                className={prov.esDocente ? "table-info" : ""}
                onClick={() => onVerDetalle(c)}>
                <td>
                  <div className="fw-semibold d-flex align-items-center gap-2">
                    {prov.nombre || <span className="text-muted">—</span>}
                    {prov.esDocente && <i className="bi bi-mortarboard text-info" title="Docente Ademy" style={{ fontSize: 13 }} />}
                  </div>
                  {prov.identificacion && <small className="text-muted">{prov.identificacion}</small>}
                </td>
                <td>
                  <div>{c.descripcion || "—"}</div>
                  {Array.isArray(c.detalles) && c.detalles[0]?.producto && (
                    <small className="text-muted"><i className="bi bi-box me-1" />{c.detalles[0].producto}</small>
                  )}
                </td>
                <td className="text-end fw-semibold">${fmt(c.total)}</td>
                <td>{c.fecha_emision || "—"}</td>
                <td><small className="font-monospace text-muted">{c.documento || "—"}</small></td>
                <td><span className={`badge ${est.cls}`}>{est.label}</span></td>
                <td className="text-end">
                  <button type="button" className="btn btn-sm btn-outline-secondary"
                    onClick={e => { e.stopPropagation(); onVerDetalle(c); }} title="Ver detalle">
                    <i className="bi bi-eye" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Vista principal ───────────────────────────────────────────────────────────

export default function ComprasRegistradas() {
  const [tab, setTab] = useState("local");

  // Dropdown nueva compra
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [modalDocente,  setModalDocente]  = useState(false);
  const [modalGeneral,  setModalGeneral]  = useState(false);

  // Tab local
  const [compras,      setCompras]      = useState([]);
  const [empleados,    setEmpleados]    = useState([]);
  const [cursos,       setCursos]       = useState([]);
  const [productos,    setProductos]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [enviandoId,   setEnviandoId]   = useState(null);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroTipo,   setFiltroTipo]   = useState("");

  // Tab Contífico
  const [proList,    setProList]    = useState([]);
  const [proLoading, setProLoading] = useState(false);
  const [proError,   setProError]   = useState(null);
  const [proDetalle, setProDetalle] = useState(null);
  const [proFiltros, setProFiltros] = useState({ fecha_inicial: primerDiaMes(), fecha_final: hoy() });

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [dropdownOpen]);

  const cargarLocal = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroEstado) params.estado = filtroEstado;
      if (filtroTipo)   params.tipo   = filtroTipo;
      const data = await listarCompras(params);
      setCompras(Array.isArray(data) ? data : []);
    } catch {
      showToast({ type: "danger", message: "No se pudieron cargar las compras." });
    } finally {
      setLoading(false);
    }
  }, [filtroEstado, filtroTipo]);

  const cargarContifico = useCallback(async () => {
    setProLoading(true);
    setProError(null);
    try {
      const res = await listarComprasContifico({ ...proFiltros, page_size: 100 });
      setProList(Array.isArray(res?.results) ? res.results : []);
    } catch (err) {
      setProError(err.message || "Error al cargar compras desde Contífico.");
      setProList([]);
    } finally {
      setProLoading(false);
    }
  }, [proFiltros]);

  useEffect(() => { cargarLocal(); }, [cargarLocal]);

  useEffect(() => {
    listarEmpleados()
      .then(data => setEmpleados((Array.isArray(data) ? data : []).map(normalizeEmpleado)))
      .catch(() => {});
    listarCursos && listarCursos()
      .then(data => setCursos(Array.isArray(data) ? data : []))
      .catch(() => {});
    listarProductosContifico()
      .then(data => setProductos((Array.isArray(data) ? data : []).filter(p => p.activo)))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "contifico") cargarContifico();
  }, [tab, cargarContifico]);

  const handleEnviarContifico = async (compra) => {
    setEnviandoId(compra.id);
    try {
      const res = await registrarEnContifico(compra.id);
      showToast({ type: "success", message: `Registrado en Contífico: ${res.contifico_documento_id}` });
      setCompras(prev => prev.map(c => c.id === compra.id
        ? { ...c, estado: "registrado", contifico_documento_id: res.contifico_documento_id }
        : c
      ));
    } catch (err) {
      showToast({ type: "danger", message: err.message || "Error al registrar en Contífico." });
    } finally {
      setEnviandoId(null);
    }
  };

  const pendientes       = compras.filter(c => c.estado === "pendiente");
  const totalPendiente   = pendientes.reduce((s, c) => s + Number(c.monto_total || 0), 0);
  const totalRegistrado  = compras.filter(c => c.estado === "registrado").reduce((s, c) => s + Number(c.monto_total || 0), 0);
  const totalProContifico = proList.filter(c => c.estado !== "A").reduce((s, c) => s + Number(c.total || 0), 0);

  return (
    <section className="management-page management-theme-blue">
      {/* Header */}
      <div className="management-card management-header mb-4">
        <div>
          <p className="management-eyebrow mb-1">Financiero / Compras</p>
          <h4 className="management-title mb-1 d-flex align-items-center gap-2">
            <i className="bi bi-file-earmark-text" />
            Compras / Facturas recibidas
          </h4>
          <p className="text-muted mb-0">
            Registra egresos de servicios docentes y consulta todas las compras registradas en Contífico.
          </p>
        </div>

        {/* Dropdown "Nueva compra" */}
        <div className="position-relative" ref={dropdownRef}>
          <button type="button" className="btn management-btn-primary dropdown-toggle"
            onClick={() => setDropdownOpen(o => !o)}>
            <i className="bi bi-plus-circle me-2" />
            Nueva compra
          </button>
          {dropdownOpen && (
            <ul className="dropdown-menu show shadow-sm" style={{ top: "100%", right: 0, left: "auto", minWidth: 220 }}>
              <li>
                <button className="dropdown-item" onClick={() => { setDropdownOpen(false); setModalDocente(true); }}>
                  <i className="bi bi-mortarboard me-2 text-primary" />
                  Compra docente (horas)
                </button>
              </li>
              <li>
                <button className="dropdown-item" onClick={() => { setDropdownOpen(false); setModalGeneral(true); }}>
                  <i className="bi bi-bag me-2 text-success" />
                  Compra general
                </button>
              </li>
            </ul>
          )}
        </div>
      </div>

      {/* Resumen */}
      <div className="row g-3 mb-4">
        {[
          { label: "Pendientes de envío",    value: `$${totalPendiente.toFixed(2)}`,  color: "warning", icon: "bi-hourglass-split",
            extra: pendientes.length > 0 ? <span className="badge bg-warning text-dark ms-1">{pendientes.length}</span> : null },
          { label: "Enviadas a Contífico",   value: `$${totalRegistrado.toFixed(2)}`, color: "success", icon: "bi-cloud-check" },
          tab === "contifico" && { label: "Total PRO en Contífico", value: `$${totalProContifico.toFixed(2)}`, color: "primary", icon: "bi-receipt" },
        ].filter(Boolean).map(({ label, value, color, icon, extra }) => (
          <div className="col-md-3" key={label}>
            <div className={`management-card border-${color} border-start border-3 py-3`}>
              <div className="d-flex align-items-center gap-2">
                <i className={`bi ${icon} text-${color} fs-4`} />
                <div>
                  <div className="fw-bold">{value}{extra}</div>
                  <small className="text-muted">{label}</small>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pestañas */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${tab === "local" ? "active" : ""}`} onClick={() => setTab("local")}>
            <i className="bi bi-database me-2" />
            Registradas en Ademy
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === "contifico" ? "active" : ""}`} onClick={() => setTab("contifico")}>
            <i className="bi bi-cloud me-2" />
            Facturas en Contífico (PRO)
          </button>
        </li>
      </ul>

      {/* Tab local */}
      {tab === "local" && (
        <>
          <div className="management-card mb-3">
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <select className="form-select form-select-sm" style={{ maxWidth: 160 }}
                value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
                <option value="">Todos los tipos</option>
                <option value="docente">Docente</option>
                <option value="general">General</option>
              </select>
              <select className="form-select form-select-sm" style={{ maxWidth: 180 }}
                value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
                <option value="">Todos los estados</option>
                <option value="pendiente">Pendientes</option>
                <option value="registrado">Registrados</option>
              </select>
              <button className="btn btn-outline-secondary btn-sm" onClick={cargarLocal} disabled={loading}>
                <i className="bi bi-arrow-repeat me-1" />Actualizar
              </button>
            </div>
          </div>
          <div className="management-card">
            <TablaLocal compras={compras} loading={loading} enviandoId={enviandoId} onEnviar={handleEnviarContifico} />
          </div>
        </>
      )}

      {/* Tab Contífico */}
      {tab === "contifico" && (
        <>
          <div className="management-card mb-3">
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <div className="d-flex align-items-center gap-1">
                <label className="form-label mb-0 me-1 text-muted small">Desde</label>
                <input type="date" className="form-control form-control-sm" style={{ maxWidth: 150 }}
                  value={proFiltros.fecha_inicial}
                  onChange={e => setProFiltros(p => ({ ...p, fecha_inicial: e.target.value }))} />
              </div>
              <div className="d-flex align-items-center gap-1">
                <label className="form-label mb-0 me-1 text-muted small">Hasta</label>
                <input type="date" className="form-control form-control-sm" style={{ maxWidth: 150 }}
                  value={proFiltros.fecha_final}
                  onChange={e => setProFiltros(p => ({ ...p, fecha_final: e.target.value }))} />
              </div>
              <button className="btn btn-outline-secondary btn-sm" onClick={cargarContifico} disabled={proLoading}>
                <i className="bi bi-arrow-repeat me-1" />Actualizar
              </button>
            </div>
          </div>
          {proError && (
            <div className="alert alert-danger d-flex align-items-center gap-2 mb-3">
              <i className="bi bi-exclamation-triangle-fill" />{proError}
            </div>
          )}
          <div className="management-card">
            <TablaContifico compras={proList} loading={proLoading} onVerDetalle={setProDetalle} />
          </div>
        </>
      )}

      {/* Modales */}
      <NuevaCompraDocenteModal
        open={modalDocente} onClose={() => setModalDocente(false)}
        onCreada={(nueva) => setCompras(prev => [nueva, ...prev])}
        empleados={empleados} cursos={cursos}
      />
      <NuevaCompraGeneralModal
        open={modalGeneral} onClose={() => setModalGeneral(false)}
        onCreada={(nueva) => setCompras(prev => [nueva, ...prev])}
        empleados={empleados} productos={productos}
      />
      <DetallePROModal doc={proDetalle} onClose={() => setProDetalle(null)} />
    </section>
  );
}
